import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlogDocsService, BlogPostMeta } from '../../shared/blog-docs.service';
import { CtaSectionComponent } from '../../components/cta-section/cta-section.component';

interface BlogListVm {
  allPosts: BlogPostMeta[];
  allTags: string[];
  latestPost: BlogPostMeta | null;
  query: string;
  searchResults: BlogPostMeta[] | null;
}

@Component({
  selector: 'app-blog-list',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CtaSectionComponent],
  templateUrl: './blog-list.component.html',
  styleUrls: ['./blog-list.component.scss'],
})
export class BlogListComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly docs = inject(BlogDocsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  // Tracks current carousel page index per tag
  tagCarouselPages: Record<string, number> = {};

  readonly PAGE_SIZE = 3;

  // Tag accent colours (cycles if more tags than colours)
  private readonly TAG_COLOURS = [
    '#e03b3b', '#e08c1a', '#2f6fed', '#c832d8',
    '#16a34a', '#0891b2', '#9333ea', '#db2777',
  ];

  readonly vm$ = combineLatest([
    this.docs.getAllPosts(),
    this.docs.getTagMap(),
    this.route.queryParamMap.pipe(
      map((q) => ({ q: (q.get('q') || '').trim() }))
    ),
    this.searchControl.valueChanges.pipe(startWith(''), debounceTime(150), distinctUntilChanged()),
  ]).pipe(
    map(([posts, tagMap, qp, typed]) => {
      const query = (typed || qp.q || '').trim();
      const qLower = query.toLowerCase();

      const searchResults: BlogPostMeta[] | null = qLower
        ? posts.filter((p) => {
            const hay = `${p.title} ${p.slug} ${p.excerpt} ${(p.tagIds || []).join(' ')}`.toLowerCase();
            return hay.includes(qLower);
          })
        : null;

      return {
        allPosts: posts,
        allTags: Object.keys(tagMap || {}).sort((a, b) => a.localeCompare(b)),
        latestPost: posts[0] ?? null,
        query,
        searchResults,
      } satisfies BlogListVm;
    })
  );

  constructor() {
    // Sync search input ↔ URL query param
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { q: (value || '').trim() || null },
          queryParamsHandling: 'merge',
        });
      });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => {
      const fromUrl = (q.get('q') || '').trim();
      if (fromUrl !== this.searchControl.value) this.searchControl.setValue(fromUrl, { emitEvent: false });
    });
  }

  // ── Tag colour helper ────────────────────────────────────────────────────────
  getTagColour(index: number): string {
    return this.TAG_COLOURS[index % this.TAG_COLOURS.length];
  }

  // ── Carousel helpers ─────────────────────────────────────────────────────────
  getTagPosts(tag: string, allPosts: BlogPostMeta[]): BlogPostMeta[] {
    return allPosts.filter((p) => (p.tagIds || []).includes(tag));
  }

  getCarouselPosts(tag: string, allPosts: BlogPostMeta[]): BlogPostMeta[] {
    const tagPosts = this.getTagPosts(tag, allPosts);
    const page = this.tagCarouselPages[tag] ?? 0;
    return tagPosts.slice(page * this.PAGE_SIZE, (page + 1) * this.PAGE_SIZE);
  }

  canGoPrev(tag: string): boolean {
    return (this.tagCarouselPages[tag] ?? 0) > 0;
  }

  canGoNext(tag: string, allPosts: BlogPostMeta[]): boolean {
    const total = this.getTagPosts(tag, allPosts).length;
    const maxPage = Math.max(0, Math.ceil(total / this.PAGE_SIZE) - 1);
    return (this.tagCarouselPages[tag] ?? 0) < maxPage;
  }

  carouselPrev(tag: string): void {
    const cur = this.tagCarouselPages[tag] ?? 0;
    if (cur > 0) this.tagCarouselPages = { ...this.tagCarouselPages, [tag]: cur - 1 };
  }

  carouselNext(tag: string, allPosts: BlogPostMeta[]): void {
    const total = this.getTagPosts(tag, allPosts).length;
    const maxPage = Math.max(0, Math.ceil(total / this.PAGE_SIZE) - 1);
    const cur = this.tagCarouselPages[tag] ?? 0;
    if (cur < maxPage) this.tagCarouselPages = { ...this.tagCarouselPages, [tag]: cur + 1 };
  }

  // ── Actions ──────────────────────────────────────────────────────────────────
  open(post: BlogPostMeta): void {
    window.location.href = `/blog/${post.slug}`;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  scrollToTag(tag: string): void {
    if (typeof document === 'undefined' || !tag) return;
    const el = document.getElementById(`bl-section-${CSS.escape(tag)}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  trackBySlug(_: number, item: BlogPostMeta): string {
    return item.slug;
  }
}
